/**
 * SoleadLab 2.0 — Review Tools Ticket (Left Rail)
 *
 * Temporary exposure surface for orphaned data-review tools:
 * Evidence, Observations, Findings, Claims, and Asset Tree.
 *
 * Each tool requires a server-side siteId — shows a clear message
 * when no site is loaded. Tabs switch between the different views.
 *
 * Also provides navigation links to /monitoring and /iv standalone pages.
 */

import { useState } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import EvidenceList from '@/components/evidence/EvidenceList';
import ObservationList from '@/components/observations/ObservationList';
import FindingList from '@/components/findings/FindingList';
import ClaimList from '@/components/claims/ClaimList';
import AssetTree from '@/components/assets/AssetTree';
import '@/styles/review-tools.css';

type TabId = 'assets' | 'evidence' | 'observations' | 'findings' | 'claims';

const TABS: { id: TabId; label: string }[] = [
  { id: 'assets', label: 'Assets' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'observations', label: 'Observations' },
  { id: 'findings', label: 'Findings' },
  { id: 'claims', label: 'Claims' },
];

export default function ReviewToolsTicket() {
  const siteId = useProjectStore((s) => s.meta.siteId);
  const [activeTab, setActiveTab] = useState<TabId>('assets');

  return (
    <div className="review-tools-ticket">
      {/* ── Tab bar ── */}
      <div className="review-tools-ticket__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`review-tools-ticket__tab${activeTab === tab.id ? ' review-tools-ticket__tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className="review-tools-ticket__content">
        {!siteId ? (
          <div className="review-tools-ticket__no-site">
            No server-side site loaded. Open or create a project to use review tools.
          </div>
        ) : (
          <>
            {activeTab === 'assets' && <AssetTree siteId={siteId} />}
            {activeTab === 'evidence' && <EvidenceList siteId={siteId} />}
            {activeTab === 'observations' && <ObservationList siteId={siteId} />}
            {activeTab === 'findings' && <FindingList siteId={siteId} />}
            {activeTab === 'claims' && <ClaimList siteId={siteId} />}
          </>
        )}
      </div>

      {/* ── Navigation links to standalone pages ── */}
      <div className="review-tools-ticket__nav-links">
        <a
          href="/monitoring"
          target="_blank"
          rel="noopener noreferrer"
          className="review-tools-ticket__nav-link"
        >
          Open Monitoring Dashboard
        </a>
        <a
          href="/iv"
          target="_blank"
          rel="noopener noreferrer"
          className="review-tools-ticket__nav-link"
        >
          Open IV Reporting Page
        </a>
      </div>
    </div>
  );
}
